/**
 * Public visual SNAPSHOT / VERSION types + pure diff (Macroblock #17, Phase B).
 *
 * The backend `GET /api/v1/snapshot/region` returns, for every active territory
 * intersecting a viewport, an opaque deterministic `visual_version`. This module
 * owns the PURE comparison logic the viewer uses to decide — precisely and
 * without any global cache bust — WHICH territories' rendered content is stale.
 */

export interface TerritorySnapshotRow {
  identity_id: string;
  display_name: string | null;
  visual_version: string;
  identity_revision: number;
  has_publication: boolean;
  has_brand_image: boolean;
  placement_count: number;
  member_parcel_ids: number[];
}

export interface RegionSnapshot {
  contract_version: string;
  region_version: string;
  territories: TerritorySnapshotRow[];
  count: number;
}

export interface SnapshotDiff {
  /** identity_ids whose visual_version changed (content must be re-fetched). */
  changed: string[];
  /** identity_ids present now but not before (newly visible / newly published). */
  added: string[];
  /** identity_ids gone now (withdrawn / left viewport → drop cached content). */
  removed: string[];
  /** Convenience: region checksum differs (something in the region changed). */
  regionChanged: boolean;
}

const EMPTY: SnapshotDiff = { changed: [], added: [], removed: [], regionChanged: false };

function indexByIdentity(rows: TerritorySnapshotRow[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const r of rows) m.set(r.identity_id, r.visual_version);
  return m;
}

/**
 * Deterministic per-territory diff. `changed`/`added`/`removed` are the authoritative
 * invalidation set; a change in ONE territory never lists unrelated territories
 * (no global bust). Sorted for stability.
 */
export function diffSnapshots(
  prev: RegionSnapshot | null,
  next: RegionSnapshot | null,
): SnapshotDiff {
  if (next == null) return EMPTY;
  const prevMap = prev ? indexByIdentity(prev.territories) : new Map<string, string>();
  const nextMap = indexByIdentity(next.territories);

  const changed: string[] = [];
  const added: string[] = [];
  const removed: string[] = [];

  for (const [id, ver] of nextMap) {
    if (!prevMap.has(id)) added.push(id);
    else if (prevMap.get(id) !== ver) changed.push(id);
  }
  for (const id of prevMap.keys()) {
    if (!nextMap.has(id)) removed.push(id);
  }

  const regionChanged = (prev?.region_version ?? null) !== next.region_version;
  return {
    changed: changed.sort(),
    added: added.sort(),
    removed: removed.sort(),
    regionChanged,
  };
}

/** True iff the diff carries any actionable invalidation. */
export function diffHasChanges(d: SnapshotDiff): boolean {
  return d.changed.length > 0 || d.added.length > 0 || d.removed.length > 0;
}
